import type { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-enterprise';
import moment from 'moment/moment';
import { useTranslation } from 'react-i18next';
import type { InvestmentStrategy } from '../../../app/api/generated/client';
import { DEFAULT_INPUT_DATE_FORMAT, investmentStrategyMap } from '../../../app/config/constants';
import { formatAmountCurrency } from '../../../app/utils/renderUtils';
import { concatClean, formatPortfolioNumber } from '../../../app/utils/utils';
import type { LiquidityOverviewTableRow } from '../../interfaces';
import { LiquidityOverviewActionsCell } from './liquidity-overview-actions-cell';

export function useLiquidityOverviewTableColDefs(): ColDef<LiquidityOverviewTableRow>[] {
    const { t } = useTranslation();

    return [
        {
            field: 'name',
            headerName: t('liquidityOverviewTable.column.name'),
            sortable: true,
            resizable: true,
            width: 280,
            cellRenderer: ({ value, data }: ICellRendererParams<LiquidityOverviewTableRow>) =>
                concatClean(value ?? '', formatPortfolioNumber(data?.portfolioNumber, data?.businessRelationId, data?.qualifiedId.id)),
        },
        {
            field: 'serviceModel',
            headerName: t('liquidityOverviewTable.column.serviceModel'),
            sortable: true,
            resizable: true,
            flex: 1,
            minWidth: 160,
            valueFormatter: ({ value, data }: ValueFormatterParams<LiquidityOverviewTableRow>) => {
                if (data?.source === 'EXTERNAL') return data.balanceEntryType ? t(data.balanceEntryType) : '';

                return value ?? '-';
            },
        },
        {
            field: 'investmentStrategy',
            headerName: t('liquidityOverviewTable.column.strategy'),
            sortable: true,
            resizable: true,
            flex: 1,
            minWidth: 140,
            valueFormatter: ({ value }: ValueFormatterParams<LiquidityOverviewTableRow, InvestmentStrategy>) => (value ? t(investmentStrategyMap[value]) : '-'),
        },
        {
            field: 'updatedAt',
            headerName: t('liquidityOverviewTable.column.lastUpdated'),
            sortable: true,
            resizable: true,
            width: 150,
            headerClass: 'ag-right-aligned-header',
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
            valueFormatter: ({ value }: ValueFormatterParams<LiquidityOverviewTableRow, string>) =>
                value && moment(value).isValid() ? moment(value).format(DEFAULT_INPUT_DATE_FORMAT) : '-',
        },
        {
            field: 'currency',
            headerName: t('liquidityOverviewTable.column.portfolioCurrency'),
            sortable: true,
            resizable: true,
            width: 140,
        },
        {
            field: 'marketValue',
            headerName: t('liquidityOverviewTable.column.marketValuePortfolioCurrency'),
            sortable: true,
            resizable: true,
            flex: 1,
            minWidth: 150,
            headerClass: 'ag-right-aligned-header',
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
            valueFormatter: ({ value, data }: ValueFormatterParams<LiquidityOverviewTableRow>) => formatAmountCurrency(value, data?.currency ?? '') || '-',
        },
        {
            field: 'referenceMarketValue',
            headerName: t('liquidityOverviewTable.column.marketValueCHF'),
            sortable: true,
            resizable: true,
            flex: 1,
            minWidth: 150,
            headerClass: 'ag-right-aligned-header',
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
            valueFormatter: ({ value, data }: ValueFormatterParams<LiquidityOverviewTableRow>) =>
                formatAmountCurrency(value, data?.referenceCurrency ?? '') || '-',
        },
        {
            field: 'currentCash',
            headerName: t('liquidityOverviewTable.column.cashCHF'),
            sortable: true,
            resizable: true,
            flex: 1,
            minWidth: 150,
            headerClass: 'ag-right-aligned-header',
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
            valueFormatter: ({ value, data }: ValueFormatterParams<LiquidityOverviewTableRow>) =>
                formatAmountCurrency(value, data?.referenceCurrency ?? '') || '-',
        },
        {
            headerName: t('liquidityOverviewTable.column.actions'),
            sortable: false,
            resizable: false,
            suppressHeaderMenuButton: true,
            width: 75,
            headerClass: 'ag-right-aligned-header',
            cellStyle: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'end',
                textAlign: 'right',
                borderLeft: '1px solid var(--border-color-default-normal)',
            },
            cellRenderer: LiquidityOverviewActionsCell,
        },
    ];
}
